"use client"

import { useMemo } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { useExperimentVariant } from "@/components/analytics/experiment"
import { SCOPES, type ScopeKey } from "@/lib/chat-scopes"

/**
 * Resolves the active A/B variant for a given chat scope and returns the
 * starter prompts that match. Variants are keyed `prompts-<variant>` in
 * the `chat.scopes.<resolveScope>` namespace; the control (unsuffixed
 * `prompts`) is always the fallback, and `scopes.tests.prompts` is the
 * final generic fallback when the resolved scope has no prompts at all.
 *
 * Flag convention (must match PostHog): `chat-prompts-<resolveScope>`,
 * where `resolveScope = promptScope ?? scope`. Pass `promptScope` to key
 * on a page slug (e.g. "calcium-score") while keeping `scope` as the Dify
 * routing scope ("tests" / "fullbodymri"). Omit `promptScope` for legacy
 * callers — behaviour is unchanged.
 *
 * E.g. `chat-prompts-calcium-score`, `chat-prompts-general`.
 *
 * New copy variants ship as additional translation keys in the PR that
 * introduces the experiment — PostHog then simply routes traffic between
 * them. No payload / runtime content on PostHog.
 */
export interface PromptVariantResult {
  prompts: string[]
  variant: string
  /** Used in analytics to disambiguate which prompt set fired. */
  variantId: string
}

export function usePromptVariant(
  scope: string,
  promptScope?: string
): PromptVariantResult {
  const tChat = useTranslations("chat")
  // Validate the Dify routing `scope` against the known SCOPES (unknown →
  // "general"), then let an explicit `promptScope` page-slug key override it.
  const validatedScope: ScopeKey = (
    scope in SCOPES ? scope : "general"
  ) as ScopeKey
  const resolveScope = promptScope ?? validatedScope
  const flag = `chat-prompts-${resolveScope}`
  const variant = useExperimentVariant(flag, "control") ?? "control"

  return useMemo(() => {
    const read = (key: string): string[] | null => {
      // Probe optional keys with `has` before `raw`: the variant key, the
      // per-scope control key and the generic `tests` fallback are each
      // intentionally absent for some scopes/locales, and the fallback chain
      // below handles a miss. next-intl's `raw` still reports every miss to
      // Sentry as MISSING_MESSAGE via `onError` (it swallows the error
      // internally, so the old try/catch never fired); `has` checks existence
      // without reporting.
      if (!(tChat.has as (k: string) => boolean)(key)) return null
      const raw = (tChat.raw as (k: string) => unknown)(key)
      return Array.isArray(raw) && raw.every((x) => typeof x === "string")
        ? (raw as string[])
        : null
    }
    const variantKey = `scopes.${resolveScope}.prompts-${variant}`
    const controlKey = `scopes.${resolveScope}.prompts`
    const chosen = variant !== "control" ? read(variantKey) : null
    const prompts =
      chosen ?? read(controlKey) ?? read("scopes.tests.prompts") ?? []
    const resolvedVariant = chosen ? variant : "control"
    return {
      prompts,
      variant: resolvedVariant,
      variantId: `${flag}:${resolvedVariant}`,
    }
  }, [tChat, resolveScope, variant, flag])
}
